import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    collection,
    query,
    orderBy,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    Timestamp,
    arrayUnion,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import {
    MagnifyingGlassIcon,
    XMarkIcon,
    PlusIcon,
    PencilSquareIcon,
    TrashIcon,
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    UserGroupIcon,
    ChevronDownIcon,
    EnvelopeIcon,
    PhoneIcon,
    BuildingOfficeIcon,
    CheckCircleIcon,
} from "@heroicons/react/24/outline";

type ContactStatus =
    | "lead"
    | "prospect"
    | "qualified"
    | "proposal"
    | "negotiation"
    | "closed_won"
    | "closed_lost"
    | "churned";

type ContactSource = "manual" | "csv_import" | "referral" | "website" | "other";

type CustomerType = "athlete" | "coach" | "organisation";

interface ContactNote {
    id: string;
    text: string;
    createdByUid: string;
    createdByEmail: string;
    createdByName: string;
    createdAt: Timestamp;
}

interface CRMContact {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    company: string;
    title: string;
    status: ContactStatus;
    source: ContactSource;
    customerType: CustomerType;
    notes: ContactNote[];
    value: number;
    createdAt: Timestamp | null;
    updatedAt: Timestamp | null;
    createdByUid?: string;
    createdByEmail?: string;
    createdByName?: string;
}

type ContactFormData = Omit<CRMContact, "id" | "createdAt" | "updatedAt" | "notes">;

const STATUS_OPTIONS: { value: ContactStatus; label: string; color: string }[] = [
    { value: "lead",        label: "Lead",         color: "bg-gray-100 text-gray-700" },
    { value: "prospect",    label: "Prospect",     color: "bg-blue-100 text-blue-700" },
    { value: "qualified",   label: "Qualified",    color: "bg-purple-100 text-purple-700" },
    { value: "proposal",    label: "Proposal",     color: "bg-yellow-100 text-yellow-700" },
    { value: "negotiation", label: "Negotiation",  color: "bg-orange-100 text-orange-700" },
    { value: "closed_won",  label: "Closed Won",   color: "bg-green-100 text-green-700" },
    { value: "closed_lost", label: "Closed Lost",  color: "bg-red-100 text-red-700" },
    { value: "churned",     label: "Churned",      color: "bg-gray-200 text-gray-500" },
];

const SOURCE_OPTIONS: { value: ContactSource; label: string }[] = [
    { value: "manual",     label: "Manual Entry" },
    { value: "csv_import", label: "CSV Import" },
    { value: "referral",   label: "Referral" },
    { value: "website",    label: "Website" },
    { value: "other",      label: "Other" },
];

const CUSTOMER_TYPE_OPTIONS: { value: CustomerType; label: string; color: string }[] = [
    { value: "athlete",      label: "Athlete",      color: "bg-sky-100 text-sky-700" },
    { value: "coach",        label: "Coach",        color: "bg-teal-100 text-teal-700" },
    { value: "organisation", label: "Organisation", color: "bg-indigo-100 text-indigo-700" },
];

const EMPTY_FORM: ContactFormData = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    title: "",
    status: "lead",
    source: "manual",
    customerType: "athlete",
    value: 0,
};

const CRMPage: React.FC = () => {
    const { currentUser } = useAuth();
    const [contacts, setContacts] = useState<CRMContact[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState<string | null>(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState<ContactStatus | "all">("all");
    const [myContactsOnly, setMyContactsOnly] = useState(false);
    const [adminProfiles, setAdminProfiles] = useState<Record<string, { name: string; imageUrl?: string }>>({});
    const [selectedContact, setSelectedContact] = useState<CRMContact | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editingContact, setEditingContact] = useState<CRMContact | null>(null);
    const [formData, setFormData] = useState<ContactFormData>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});

    const [newNoteText, setNewNoteText] = useState("");
    const [savingNote, setSavingNote] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState<{ success: number; errors: number } | null>(null);
    const [showImportGuide, setShowImportGuide] = useState(false);

    const loadContacts = useCallback(async () => {
        try {
            setLoading(true);
            const ref = collection(db, "crm");
            const q = query(ref, orderBy("createdAt", "desc"));
            const snapshot = await getDocs(q);
            const list: CRMContact[] = [];
            snapshot.forEach((d) => {
                list.push({ id: d.id, ...d.data() } as CRMContact);
            });
            setContacts(list);

            // Fetch admin profiles for unique createdByUid values
            const uids = Array.from(new Set(list.map((c) => c.createdByUid).filter(Boolean))) as string[];
            if (uids.length > 0) {
                const profiles: Record<string, { name: string; imageUrl?: string }> = {};
                await Promise.all(
                    uids.map(async (uid) => {
                        try {
                            const [privateDoc, publicDoc] = await Promise.all([
                                getDoc(doc(db, "users", uid, "private", "data")),
                                getDoc(doc(db, "users", uid)),
                            ]);
                            const data = privateDoc.data();
                            const publicData = publicDoc.data();
                            const name =
                                data?.displayName ||
                                (data?.name && data?.surname ? `${data.name} ${data.surname}` : null) ||
                                data?.email ||
                                list.find((c) => c.createdByUid === uid)?.createdByEmail ||
                                uid;
                            profiles[uid] = { name, imageUrl: publicData?.imageUrl ?? undefined };
                        } catch {
                            profiles[uid] = {
                                name: list.find((c) => c.createdByUid === uid)?.createdByEmail || uid,
                            };
                        }
                    })
                );
                setAdminProfiles(profiles);
            }
        } catch (error) {
            console.error("Error loading CRM contacts:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = "CRM - Peak^ Admin";
        loadContacts();
    }, [loadContacts]);

    const filteredContacts = contacts.filter((c) => {
        const matchesSearch =
            !searchTerm ||
            `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (c.company || "").toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === "all" || c.status === statusFilter;
        const matchesMine = !myContactsOnly || c.createdByUid === currentUser?.uid;
        return matchesSearch && matchesStatus && matchesMine;
    });

    const stats = {
        total: contacts.length,
        leads: contacts.filter((c) => c.status === "lead" || c.status === "prospect").length,
        pipeline: contacts.filter((c) =>
            ["qualified", "proposal", "negotiation"].includes(c.status)
        ).length,
        closedWon: contacts.filter((c) => c.status === "closed_won").length,
        pipelineValue: contacts
            .filter((c) => !["closed_won", "closed_lost", "churned"].includes(c.status))
            .reduce((sum, c) => sum + (c.value || 0), 0),
        revenueWon: contacts
            .filter((c) => c.status === "closed_won")
            .reduce((sum, c) => sum + (c.value || 0), 0),
    };

    const validateForm = (): boolean => {
        const errors: Partial<Record<keyof ContactFormData, string>> = {};

        if (!formData.email.trim()) {
            errors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = "Invalid email address";
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        setSaving(true);
        try {
            if (editingContact) {
                const ref = doc(db, "crm", editingContact.id);
                await updateDoc(ref, { ...formData, updatedAt: serverTimestamp() });
                setContacts((prev) =>
                    prev.map((c) => (c.id === editingContact.id ? { ...c, ...formData } : c))
                );
                if (selectedContact?.id === editingContact.id) {
                    setSelectedContact((prev) => (prev ? { ...prev, ...formData } : null));
                }
            } else {
                const ref = collection(db, "crm");
                const createdByName = currentUser?.displayName ?? currentUser?.email ?? undefined;
                const docRef = await addDoc(ref, {
                    ...formData,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    createdByUid: currentUser?.uid ?? null,
                    createdByEmail: currentUser?.email ?? null,
                    createdByName: createdByName ?? null,
                });
                if (currentUser?.uid && createdByName) {
                    setAdminProfiles((prev) => ({ ...prev, [currentUser.uid]: { name: createdByName } }));
                }
                const newContact: CRMContact = {
                    id: docRef.id,
                    ...formData,
                    notes: [],
                    createdAt: null,
                    updatedAt: null,
                    createdByUid: currentUser?.uid ?? undefined,
                    createdByEmail: currentUser?.email ?? undefined,
                    createdByName,
                };
                setContacts((prev) => [newContact, ...prev]);
            }
            closeForm();
        } catch (error) {
            console.error("Error saving contact:", error);
        } finally {
            setSaving(false);
        }
    };

    const handleAddNote = async () => {
        if (!newNoteText.trim() || !selectedContact || !currentUser) return;
        setSavingNote(true);
        try {
            const note: ContactNote = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                text: newNoteText.trim(),
                createdByUid: currentUser.uid,
                createdByEmail: currentUser.email ?? "",
                createdByName: currentUser.displayName ?? currentUser.email ?? "",
                createdAt: Timestamp.now(),
            };
            await updateDoc(doc(db, "crm", selectedContact.id), {
                notes: arrayUnion(note),
                updatedAt: serverTimestamp(),
            });
            const updated = { ...selectedContact, notes: [...(selectedContact.notes ?? []), note] };
            setSelectedContact(updated);
            setContacts((prev) => prev.map((c) => (c.id === selectedContact.id ? updated : c)));
            setNewNoteText("");
        } catch (error) {
            console.error("Error adding note:", error);
        } finally {
            setSavingNote(false);
        }
    };

    const handleDelete = async (contact: CRMContact) => {
        if (!window.confirm(`Delete ${contact.firstName} ${contact.lastName}? This cannot be undone.`)) return;
        setDeleting(contact.id);
        try {
            await deleteDoc(doc(db, "crm", contact.id));
            setContacts((prev) => prev.filter((c) => c.id !== contact.id));
            if (selectedContact?.id === contact.id) setSelectedContact(null);
        } catch (error) {
            console.error("Error deleting contact:", error);
        } finally {
            setDeleting(null);
        }
    };

    const handleEdit = (contact: CRMContact) => {
        setEditingContact(contact);
        setFormData({
            firstName: contact.firstName || "",
            lastName: contact.lastName || "",
            email: contact.email || "",
            phone: contact.phone || "",
            company: contact.company || "",
            title: contact.title || "",
            status: contact.status,
            source: contact.source || "manual",
            customerType: contact.customerType || "athlete",
            value: contact.value || 0,
        });
        setFormErrors({});
        setShowForm(true);
    };

    const openAddForm = () => {
        setEditingContact(null);
        setFormData(EMPTY_FORM);
        setFormErrors({});
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingContact(null);
        setFormData(EMPTY_FORM);
        setFormErrors({});
    };

    const handleExport = () => {
        const headers = [
            "First Name", "Last Name", "Email", "Phone",
            "Company", "Title", "Customer Type", "Status", "Source", "Value", "Notes",
        ];
        const rows = filteredContacts.map((c) => [
            c.firstName,
            c.lastName,
            c.email,
            c.phone || "",
            c.company || "",
            c.title || "",
            CUSTOMER_TYPE_OPTIONS.find((t) => t.value === c.customerType)?.label || c.customerType || "",
            STATUS_OPTIONS.find((s) => s.value === c.status)?.label || c.status,
            SOURCE_OPTIONS.find((s) => s.value === c.source)?.label || c.source,
            (c.value || 0).toString(),
            (Array.isArray(c.notes) ? c.notes.map((n) => n.text).join(" | ") : "").replace(/"/g, '""').replace(/\n/g, " "),
        ]);

        const csvContent = [headers, ...rows]
            .map((row) => row.map((cell) => `"${cell}"`).join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `crm-contacts-${new Date().toISOString().split("T")[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleImportClick = () => setShowImportGuide(true);
    const handleImportConfirm = () => { setShowImportGuide(false); fileInputRef.current?.click(); };

    const parseCSVLine = (line: string): string[] => {
        const result: string[] = [];
        let current = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === "," && !inQuotes) {
                result.push(current);
                current = "";
            } else {
                current += char;
            }
        }
        result.push(current);
        return result;
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImporting(true);
        setImportResult(null);

        try {
            const text = await file.text();
            const lines = text.split("\n").filter((l) => l.trim());
            if (lines.length < 2) return;

            const rawHeader = lines[0].replace(/"/g, "").toLowerCase();
            const cols = rawHeader.split(",").map((h) => h.trim());

            let successCount = 0;
            let errorCount = 0;

            for (let i = 1; i < lines.length; i++) {
                try {
                    const values = parseCSVLine(lines[i]);
                    const get = (name: string) => {
                        const idx = cols.indexOf(name);
                        return idx >= 0 ? (values[idx] || "").replace(/^"|"$/g, "").trim() : "";
                    };

                    const firstName = get("first name");
                    const lastName = get("last name");
                    const email = get("email");

                    if (!firstName && !lastName && !email) continue;

                    const statusLabel = get("status").toLowerCase();
                    const statusMatch = STATUS_OPTIONS.find(
                        (s) => s.label.toLowerCase() === statusLabel || s.value === statusLabel
                    );
                    const status: ContactStatus = statusMatch?.value ?? "lead";

                    const customerTypeLabel = get("customer type").toLowerCase();
                    const customerTypeMatch = CUSTOMER_TYPE_OPTIONS.find(
                        (t) => t.label.toLowerCase() === customerTypeLabel || t.value === customerTypeLabel
                    );
                    const customerType: CustomerType = customerTypeMatch?.value ?? "athlete";

                    const contactData = {
                        firstName,
                        lastName,
                        email,
                        phone: get("phone"),
                        company: get("company"),
                        title: get("title"),
                        status,
                        source: "csv_import" as ContactSource,
                        customerType,
                        notes: get("notes")
                            ? [{
                                id: `${Date.now()}-0`,
                                text: get("notes"),
                                createdByUid: currentUser?.uid ?? "",
                                createdByEmail: currentUser?.email ?? "",
                                createdByName: currentUser?.displayName ?? currentUser?.email ?? "",
                                createdAt: Timestamp.now(),
                            }]
                            : [],
                        value: parseFloat(get("value")) || 0,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                        createdByUid: currentUser?.uid ?? null,
                        createdByEmail: currentUser?.email ?? null,
                        createdByName: currentUser?.displayName ?? currentUser?.email ?? null,
                    };

                    const ref = collection(db, "crmContacts");
                    const docRef = await addDoc(ref, contactData);
                    setContacts((prev) => [
                        {
                            id: docRef.id,
                            ...contactData,
                            notes: contactData.notes as ContactNote[],
                            createdAt: null,
                            updatedAt: null,
                            createdByUid: currentUser?.uid ?? undefined,
                            createdByEmail: currentUser?.email ?? undefined,
                            createdByName: currentUser?.displayName ?? currentUser?.email ?? undefined,
                        } as CRMContact,
                        ...prev,
                    ]);
                    successCount++;
                } catch {
                    errorCount++;
                }
            }

            setImportResult({ success: successCount, errors: errorCount });
        } catch (error) {
            console.error("Error importing CSV:", error);
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const getStatusBadge = (status: ContactStatus) => {
        const s = STATUS_OPTIONS.find((o) => o.value === status);
        return (
            <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${s?.color ?? "bg-gray-100 text-gray-700"}`}>
                {s?.label ?? status}
            </span>
        );
    };

    const getCustomerTypeBadge = (type: CustomerType) => {
        const t = CUSTOMER_TYPE_OPTIONS.find((o) => o.value === type);
        return (
            <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${t?.color ?? "bg-gray-100 text-gray-700"}`}>
                {t?.label ?? type}
            </span>
        );
    };

    const formatDate = (ts: Timestamp | null) => {
        if (!ts) return "—";
        try {
            return ts.toDate().toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        } catch {
            return "—";
        }
    };

    return (
        <DashboardWrapper>
            <div className="w-full h-[calc(100vh-64px)] overflow-hidden p-6 flex flex-col gap-4">

                {/* Page header */}
                <div className="flex items-center justify-between shrink-0">
                    <h1 className="text-2xl font-heading">CRM</h1>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleImportClick}
                            disabled={false}
                            className="flex items-center gap-2 px-4 py-2 text-sm border rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            <ArrowUpTrayIcon className="w-4 h-4" />
                            {importing ? "Importing..." : "Import CSV"}
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={filteredContacts.length === 0}
                            className="flex items-center gap-2 px-4 py-2 text-sm border rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            Export CSV
                        </button>
                        <button
                            onClick={openAddForm}
                            className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-black transition-colors"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Add Contact
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            className="hidden"
                            onChange={handleFileChange}
                        />
                    </div>
                </div>

                {/* Import result banner */}
                {importResult && (
                    <div className="flex items-center justify-between px-4 py-2 bg-green-50 border border-green-200 rounded-lg shrink-0">
                        <span className="text-sm text-green-700">
                            Imported {importResult.success} contact{importResult.success !== 1 ? "s" : ""}
                            {importResult.errors > 0 && ` (${importResult.errors} row${importResult.errors !== 1 ? "s" : ""} skipped)`}
                        </span>
                        <button
                            onClick={() => setImportResult(null)}
                            className="text-green-600 hover:text-green-800"
                        >
                            <XMarkIcon className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Stats row */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 shrink-0">
                    <div className="bg-white rounded-xl flex flex-col p-4 gap-1 border">
                        <div className="text-2xl font-bold text-primary">{stats.total.toLocaleString()}</div>
                        <div className="text-xs text-gray-500 uppercase">Total</div>
                    </div>
                    <div className="bg-white rounded-xl flex flex-col p-4 gap-1 border">
                        <div className="text-2xl font-bold text-blue-600">{stats.leads.toLocaleString()}</div>
                        <div className="text-xs text-gray-500 uppercase">Leads</div>
                    </div>
                    <div className="bg-white rounded-xl flex flex-col p-4 gap-1 border">
                        <div className="text-2xl font-bold text-purple-600">{stats.pipeline.toLocaleString()}</div>
                        <div className="text-xs text-gray-500 uppercase">In Pipeline</div>
                    </div>
                    <div className="bg-white rounded-xl flex flex-col p-4 gap-1 border">
                        <div className="text-2xl font-bold text-green-600">{stats.closedWon.toLocaleString()}</div>
                        <div className="text-xs text-gray-500 uppercase">Closed Won</div>
                    </div>
                    <div className="bg-white rounded-xl flex flex-col p-4 gap-1 border">
                        <div className="text-xl font-bold text-gray-700">${stats.pipelineValue.toLocaleString()}</div>
                        <div className="text-xs text-gray-500 uppercase">Pipeline Value</div>
                    </div>
                    <div className="bg-white rounded-xl flex flex-col p-4 gap-1 border">
                        <div className="text-xl font-bold text-green-700">${stats.revenueWon.toLocaleString()}</div>
                        <div className="text-xs text-gray-500 uppercase">Revenue Won</div>
                    </div>
                </div>

                {/* Search + filter */}
                <div className="flex gap-2 shrink-0">
                    <div className="relative flex-1">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, email, or company..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-9 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <XMarkIcon className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    <div className="relative">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as ContactStatus | "all")}
                            className="appearance-none pl-3 pr-8 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer"
                        >
                            <option value="all">All Statuses</option>
                            {STATUS_OPTIONS.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                        <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                    <div className="flex items-center bg-white border rounded-lg p-0.5 text-sm shrink-0">
                        <button
                            onClick={() => setMyContactsOnly(true)}
                            className={`px-3 py-1.5 rounded-md transition-colors font-medium ${
                                myContactsOnly ? "bg-gray-800 text-white" : "text-gray-500 hover:text-gray-700"
                            }`}
                        >
                            My
                        </button>
                        <button
                            onClick={() => setMyContactsOnly(false)}
                            className={`px-3 py-1.5 rounded-md transition-colors font-medium ${
                                !myContactsOnly ? "bg-gray-800 text-white" : "text-gray-500 hover:text-gray-700"
                            }`}
                        >
                            All
                        </button>
                    </div>
                </div>

                {/* Table + sidebar */}
                <div className="flex-1 min-h-0 flex gap-4">

                    {/* Table */}
                    <div className="flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex flex-col">
                        <div className="grid grid-cols-7 gap-4 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0 pr-6">
                            <div className="col-span-2">Contact</div>
                            <div className="col-span-1">Company</div>
                            <div className="col-span-1">Owner</div>
                            <div className="col-span-1">Type</div>
                            <div className="col-span-1">Status</div>
                            <div className="col-span-1">Value</div>
                        </div>

                        <div className="flex flex-col h-full overflow-y-auto items-start min-h-0 relative">
                            {loading ? (
                                <span className="loading loading-spinner loading-sm absolute top-1/2 left-1/2 -translate-x-1/2"></span>
                            ) : filteredContacts.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full w-full text-gray-400 gap-3">
                                    <UserGroupIcon className="w-10 h-10 text-gray-300" />
                                    <p className="text-sm">No contacts found.</p>
                                    {contacts.length === 0 && (
                                        <button
                                            onClick={openAddForm}
                                            className="btn primary !shadow-none"
                                        >
                                            <PlusIcon className="w-4 h-4" />
                                            Add your first contact
                                        </button>
                                    )}
                                </div>
                            ) : (
                                filteredContacts.map((contact) => (
                                    <div
                                        key={contact.id}
                                        onClick={() => setSelectedContact(contact)}
                                        className={`grid w-full grid-cols-7 gap-4 px-4 py-3 border-b hover:bg-gray-50 items-center cursor-pointer transition-colors ${
                                            selectedContact?.id === contact.id
                                                ? "bg-primary/5 border-l-2 border-l-primary"
                                                : ""
                                        }`}
                                    >
                                        {/* Contact name + title */}
                                        <div className="col-span-2 flex items-center gap-2 min-w-0">
                                            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                                                {contact.firstName?.charAt(0).toUpperCase()}
                                                {contact.lastName?.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium truncate">
                                                    {contact.firstName} {contact.lastName}
                                                </div>
                                                {contact.title && (
                                                    <div className="text-xs text-gray-400 truncate">{contact.title}</div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Company */}
                                        <div className="col-span-1 text-sm text-gray-600 truncate">
                                            {contact.company || "—"}
                                        </div>

                                        {/* Owner */}
                                        <div className="col-span-1 min-w-0">
                                            {contact.createdByUid ? (() => {
                                                const profile = adminProfiles[contact.createdByUid];
                                                const name = profile?.name || contact.createdByName || contact.createdByEmail || "?";
                                                return (
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        {profile?.imageUrl ? (
                                                            <img
                                                                src={profile.imageUrl}
                                                                alt={name}
                                                                className="w-8 h-8 rounded-full object-cover shrink-0"
                                                            />
                                                        ) : (
                                                            <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-[10px] font-semibold shrink-0">
                                                                {name.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                        <span className="text-sm text-gray-600 truncate">{name}</span>
                                                    </div>
                                                );
                                            })() : (
                                                <span className="text-xs text-gray-400">—</span>
                                            )}
                                        </div>

                                        {/* Customer Type */}
                                        <div className="col-span-1">
                                            {contact.customerType && getCustomerTypeBadge(contact.customerType)}
                                        </div>

                                        {/* Status */}
                                        <div className="col-span-1">
                                            {getStatusBadge(contact.status)}
                                        </div>


                                        {/* Value */}
                                        <div className="col-span-1 text-sm text-gray-700 font-medium">
                                            {contact.value ? `$${contact.value.toLocaleString()}` : "—"}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {!loading && filteredContacts.length > 0 && (
                            <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-400 shrink-0">
                                {filteredContacts.length} contact{filteredContacts.length !== 1 ? "s" : ""}
                                {(statusFilter !== "all" || searchTerm) && contacts.length !== filteredContacts.length
                                    ? ` — filtered from ${contacts.length}`
                                    : ""}
                            </div>
                        )}
                    </div>

                    {/* Contact detail sidebar */}
                    {selectedContact && (
                        <div className="w-96 bg-white rounded-xl border flex flex-col overflow-hidden shrink-0">
                            <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                                <h2 className="text-sm font-semibold">Contact Details</h2>
                                <button
                                    onClick={() => setSelectedContact(null)}
                                    className="p-1 rounded hover:bg-gray-100 text-gray-400"
                                >
                                    <XMarkIcon className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
                                {/* Avatar + name */}
                                <div className="flex flex-col items-center gap-2 pt-1">
                                    <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl font-bold">
                                        {selectedContact.firstName?.charAt(0).toUpperCase()}
                                        {selectedContact.lastName?.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="text-center">
                                        <div className="font-semibold text-base">
                                            {selectedContact.firstName} {selectedContact.lastName}
                                        </div>
                                        {selectedContact.title && (
                                            <div className="text-xs text-gray-500">{selectedContact.title}</div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-wrap justify-center">
                                        {getStatusBadge(selectedContact.status)}
                                        {selectedContact.customerType && getCustomerTypeBadge(selectedContact.customerType)}
                                    </div>
                                </div>

                                {/* Contact info */}
                                <div className="flex flex-col gap-2">
                                    {selectedContact.email && (
                                        <a
                                            href={`mailto:${selectedContact.email}`}
                                            className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
                                        >
                                            <EnvelopeIcon className="w-4 h-4 text-gray-400 shrink-0" />
                                            <span className="truncate">{selectedContact.email}</span>
                                        </a>
                                    )}
                                    {selectedContact.phone && (
                                        <a
                                            href={`tel:${selectedContact.phone}`}
                                            className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
                                        >
                                            <PhoneIcon className="w-4 h-4 text-gray-400 shrink-0" />
                                            <span>{selectedContact.phone}</span>
                                        </a>
                                    )}
                                    {selectedContact.company && (
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <BuildingOfficeIcon className="w-4 h-4 text-gray-400 shrink-0" />
                                            <span className="truncate">{selectedContact.company}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Deal value */}
                                {(selectedContact.value ?? 0) > 0 && (
                                    <div className="bg-gray-50 rounded-lg p-3 border">
                                        <div className="text-xs text-gray-500 uppercase mb-1">Deal Value</div>
                                        <div className="text-xl font-bold text-gray-800">
                                            ${selectedContact.value.toLocaleString()}
                                        </div>
                                    </div>
                                )}

                                {/* Meta info */}
                                <div className="flex flex-col gap-1.5 text-xs">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Customer Type</span>
                                        <span className="text-gray-700">
                                            {CUSTOMER_TYPE_OPTIONS.find((t) => t.value === selectedContact.customerType)?.label ?? "—"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Source</span>
                                        <span className="text-gray-700">
                                            {SOURCE_OPTIONS.find((s) => s.value === selectedContact.source)?.label ?? selectedContact.source}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Created</span>
                                        <span className="text-gray-700">{formatDate(selectedContact.createdAt)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Updated</span>
                                        <span className="text-gray-700">{formatDate(selectedContact.updatedAt)}</span>
                                    </div>
                                </div>

                                {/* Notes */}
                                <div className="flex flex-col gap-2">
                                    <div className="text-xs font-medium text-gray-500 uppercase">Notes</div>
                                    {(selectedContact.notes?.length ?? 0) === 0 ? (
                                        <p className="text-xs text-gray-400 italic">No notes yet.</p>
                                    ) : (
                                        <div className="flex flex-col gap-2">
                                            {[...(selectedContact.notes ?? [])].sort((a, b) => {
                                                const aMs = a.createdAt?.toMillis?.() ?? 0;
                                                const bMs = b.createdAt?.toMillis?.() ?? 0;
                                                return bMs - aMs;
                                            }).map((note) => (
                                                <div key={note.id} className="bg-gray-50 rounded-lg p-3 border flex flex-col gap-1">
                                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.text}</p>
                                                    <div className="flex items-center gap-1.5 mt-1">
                                                        {adminProfiles[note.createdByUid]?.imageUrl ? (
                                                            <img
                                                                src={adminProfiles[note.createdByUid].imageUrl}
                                                                alt={note.createdByName}
                                                                className="w-4 h-4 rounded-full object-cover shrink-0"
                                                            />
                                                        ) : (
                                                            <div className="w-4 h-4 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center text-[9px] font-semibold shrink-0">
                                                                {(note.createdByName || note.createdByEmail || "?").charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                        <span className="text-[11px] text-gray-400 truncate">
                                                            {adminProfiles[note.createdByUid]?.name || note.createdByName || note.createdByEmail}
                                                            {note.createdAt && (
                                                                <> · {note.createdAt.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</>
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add note input */}
                                    <div className="flex flex-col gap-1.5 mt-1">
                                        <textarea
                                            value={newNoteText}
                                            onChange={(e) => setNewNoteText(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleAddNote();
                                            }}
                                            rows={2}
                                            placeholder="Add a note..."
                                            className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                                        />
                                        <button
                                            onClick={handleAddNote}
                                            disabled={savingNote || !newNoteText.trim()}
                                            className="self-end flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-800 text-white rounded-lg hover:bg-black transition-colors disabled:opacity-50"
                                        >
                                            {savingNote ? "Saving..." : "Add Note"}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="p-3 border-t flex gap-2 shrink-0">
                                <button
                                    onClick={() => handleEdit(selectedContact)}
                                    className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-black transition-colors"
                                >
                                    <PencilSquareIcon className="w-4 h-4" />
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(selectedContact)}
                                    disabled={deleting === selectedContact.id}
                                    className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                                    title="Delete contact"
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* CSV import guide modal */}
            {showImportGuide && (
                <div
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    onClick={() => setShowImportGuide(false)}
                >
                    <div
                        className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-4 border-b">
                            <div>
                                <h2 className="text-base font-semibold">CSV Import Format</h2>
                                <p className="text-xs text-gray-500 mt-0.5">Your file must include the following column headings</p>
                            </div>
                            <button
                                onClick={() => setShowImportGuide(false)}
                                className="p-1 rounded hover:bg-gray-100 text-gray-400"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4 flex flex-col gap-3">
                            <div className="rounded-lg border overflow-hidden text-sm">
                                <div className="grid grid-cols-12 bg-gray-50 border-b px-3 py-2 text-xs font-medium text-gray-500 uppercase">
                                    <div className="col-span-4">Column heading</div>
                                    <div className="col-span-2 text-center">Required</div>
                                    <div className="col-span-6">Notes</div>
                                </div>
                                {[
                                    { col: "First Name",    required: false, note: "" },
                                    { col: "Last Name",     required: false, note: "" },
                                    { col: "Email",         required: true,  note: "" },
                                    { col: "Phone",         required: false, note: "" },
                                    { col: "Company",       required: false, note: "" },
                                    { col: "Title",         required: false, note: "Job title or role" },
                                    { col: "Customer Type", required: false, note: "Athlete · Coach · Organisation" },
                                    { col: "Status",        required: false, note: "Lead · Prospect · Qualified · Proposal · Negotiation · Closed Won · Closed Lost · Churned" },
                                    { col: "Source",        required: false, note: "Referral · Website · Other" },
                                    { col: "Value",         required: false, note: "Numeric deal value (no $ sign)" },
                                    { col: "Notes",         required: false, note: "" },
                                ].map(({ col, required, note }) => (
                                    <div key={col} className="grid grid-cols-12 px-3 py-2 border-b last:border-b-0 items-start">
                                        <div className="col-span-4 font-mono text-xs text-gray-800">{col}</div>
                                        <div className="col-span-2 flex justify-center">
                                            {required ? (
                                                <span className="text-xs font-medium text-red-500">Yes</span>
                                            ) : (
                                                <span className="text-xs text-gray-400">No</span>
                                            )}
                                        </div>
                                        <div className="col-span-6 text-xs text-gray-500">{note}</div>
                                    </div>
                                ))}
                            </div>

                            <p className="text-xs text-gray-400">
                                Column order doesn't matter. Unrecognised statuses default to <span className="font-medium text-gray-600">Lead</span>. Contacts without an email will be skipped.
                            </p>
                        </div>

                        <div className="flex gap-2 p-4 border-t">
                            <button
                                onClick={() => setShowImportGuide(false)}
                                className="flex-1 py-2 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleImportConfirm}
                                className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-black transition-colors"
                            >
                                <ArrowUpTrayIcon className="w-4 h-4" />
                                Choose File
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add / Edit modal */}
            {showForm && (
                <div
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    onClick={closeForm}
                >
                    <div
                        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-4 border-b shrink-0">
                            <h2 className="text-base font-semibold">
                                {editingContact ? "Edit Contact" : "Add Contact"}
                            </h2>
                            <button
                                onClick={closeForm}
                                className="p-1 rounded hover:bg-gray-100 text-gray-400"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
                            {/* Name */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        First Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.firstName}
                                        onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                                        className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                                            formErrors.firstName ? "border-red-400" : "border-gray-300"
                                        }`}
                                        placeholder="John"
                                    />
                                    {formErrors.firstName && (
                                        <p className="text-xs text-red-500 mt-0.5">{formErrors.firstName}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.lastName}
                                        onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                                        className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                                            formErrors.lastName ? "border-red-400" : "border-gray-300"
                                        }`}
                                        placeholder="Doe"
                                    />
                                    {formErrors.lastName && (
                                        <p className="text-xs text-red-500 mt-0.5">{formErrors.lastName}</p>
                                    )}
                                </div>
                            </div>

                            {/* Email */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                                        formErrors.email ? "border-red-400" : "border-gray-300"
                                    }`}
                                    placeholder="john@example.com"
                                />
                                {formErrors.email && (
                                    <p className="text-xs text-red-500 mt-0.5">{formErrors.email}</p>
                                )}
                            </div>

                            {/* Phone + Company */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        placeholder="+61 444 333 222"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Company</label>
                                    <input
                                        type="text"
                                        value={formData.company}
                                        onChange={(e) => setFormData((p) => ({ ...p, company: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        placeholder="Geelong School"
                                    />
                                </div>
                            </div>

                            {/* Title */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Title / Role</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    placeholder="Head of Health & PE"
                                />
                            </div>

                            {/* Customer Type */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Customer Type</label>
                                <div className="flex gap-2">
                                    {CUSTOMER_TYPE_OPTIONS.map((t) => (
                                        <button
                                            key={t.value}
                                            type="button"
                                            onClick={() => setFormData((p) => ({ ...p, customerType: t.value }))}
                                            className={`flex-1 py-2 text-sm rounded-lg border transition-colors font-medium ${
                                                formData.customerType === t.value
                                                    ? `${t.color} border-transparent`
                                                    : "border-gray-300 text-gray-500 hover:bg-gray-50"
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Status + Source */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) =>
                                            setFormData((p) => ({ ...p, status: e.target.value as ContactStatus }))
                                        }
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
                                    >
                                        {STATUS_OPTIONS.map((s) => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Source</label>
                                    <select
                                        value={formData.source}
                                        onChange={(e) =>
                                            setFormData((p) => ({ ...p, source: e.target.value as ContactSource }))
                                        }
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
                                    >
                                        {SOURCE_OPTIONS.map((s) => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Deal Value */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Deal Value ($)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.value || ""}
                                    onChange={(e) =>
                                        setFormData((p) => ({ ...p, value: parseFloat(e.target.value) || 0 }))
                                    }
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    placeholder="0"
                                />
                            </div>

                        </div>

                        <div className="flex gap-2 p-4 border-t shrink-0">
                            <button
                                onClick={closeForm}
                                disabled={saving}
                                className="flex-1 py-2 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-black transition-colors disabled:opacity-50"
                            >
                                {saving ? (
                                    "Saving..."
                                ) : (
                                    <>
                                        <CheckCircleIcon className="w-4 h-4" />
                                        {editingContact ? "Save Changes" : "Add Contact"}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardWrapper>
    );
};

export default CRMPage;
